import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { FiLink, FiType } from "react-icons/fi";

import type { RealAssetResponse } from "@/apiClient";
import useOutsideClick from "@/hooks/useOutsideClick";

import BufferedSearchInput from "./BufferedSearchInput";
import { VariableSegmentBadge } from "./TemplatePathPills";
import {
  realAssetTypeBadgeClasses,
  realAssetTypeLabel,
  renderSegmentLabel,
  type PathSegment,
  type SegmentComposerState,
  type TokenPickerTarget,
} from "./folderConfig.utils";

export type TemplatePathBuilderProps = {
  pathSegments: PathSegment[];
  segmentComposer: SegmentComposerState;
  assetSearchQuery: string;
  filteredRealAssets: RealAssetResponse[];
  realAssetsById: Map<string, RealAssetResponse>;
  exampleDeploymentIdsByAssetId: Map<string, string>;
  onToggleChooser: () => void;
  onOpenLiteralComposer: () => void;
  onLiteralDraftChange: (value: string) => void;
  onCommitLiteral: () => void;
  onOpenRealAssetPicker: (target: TokenPickerTarget) => void;
  onInsertOrReplaceToken: (assetPublicId: string) => void;
  onCommitExistingLiteral: (segmentIndex: number, nextValue: string) => boolean;
  onRemoveSegment: (segmentIndex: number) => void;
  onCloseComposer: () => void;
  onAssetSearchChange: (value: string) => void;
};

function useEscapeToClose(isOpen: boolean, onClose: () => void) {
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);
}

const InlineLiteralSegmentEditor = memo(function InlineLiteralSegmentEditor({
  draft,
  ariaLabel,
  onDraftChange,
  onCommit,
  onCancel,
}: {
  draft: string;
  ariaLabel: string;
  onDraftChange: (value: string) => void;
  onCommit: () => void;
  onCancel: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  useLayoutEffect(() => {
    const input = inputRef.current;
    if (!input) {
      return;
    }

    input.style.width = "0px";
    input.style.width = `${Math.max(input.scrollWidth + 8, 48)}px`;
  }, [draft]);

  return (
    <div className="rounded-lg border-2 border-blue-100 bg-white px-3 py-1.5 shadow-sm">
      <input
        ref={inputRef}
        id="curated-directory-literal-segment"
        name="literal_segment"
        type="text"
        aria-label={ariaLabel}
        autoFocus
        value={draft}
        onChange={(event) => onDraftChange(event.target.value)}
        onBlur={() => {
          if (!draft.trim()) {
            onCancel();
          }
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            onCommit();
          }

          if (event.key === "Escape") {
            event.preventDefault();
            onCancel();
          }
        }}
        className="form-input block rounded-md border-0 p-0 text-sm font-semibold text-blue-300 hover:shadow-none focus:shadow-none focus:outline-none focus:ring-0 focus-visible:outline-none"
      />
    </div>
  );
});

const InlineLiteralPathSegmentInput = memo(
  function InlineLiteralPathSegmentInput({
    value,
    ariaLabel,
    onCommit,
    onRemove,
  }: {
    value: string;
    ariaLabel: string;
    onCommit: (value: string) => boolean;
    onRemove: () => void;
  }) {
    const [draft, setDraft] = useState(value);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
      setDraft(value);
    }, [value]);

    useLayoutEffect(() => {
      const input = inputRef.current;
      if (!input) {
        return;
      }

      input.style.width = "0px";
      input.style.width = `${Math.max(input.scrollWidth + 8, 64)}px`;
    }, [draft]);

    const commitDraft = useCallback(() => {
      const trimmedDraft = draft.trim();

      if (trimmedDraft === value) {
        return;
      }

      if (!trimmedDraft || !onCommit(trimmedDraft)) {
        setDraft(value);
      }
    }, [draft, onCommit, value]);

    return (
      <div className="group flex items-center gap-0.5">
        <input
          ref={inputRef}
          type="text"
          aria-label={ariaLabel}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={commitDraft}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              commitDraft();
            }

            if (event.key === "Escape") {
              event.preventDefault();
              setDraft(value);
              (event.target as HTMLInputElement).blur();
            }
          }}
          className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-blue-400 shadow-sm focus:border-blue-200 focus:outline-none"
        />
        <button
          type="button"
          className="rounded-full px-1 py-0.5 text-xs text-grey-100 opacity-0 transition-all hover:bg-slate-200 hover:text-grey-300 group-hover:opacity-100"
          aria-label={`Remove segment ${value}`}
          onMouseDown={(event) => event.preventDefault()}
          onClick={onRemove}
        >
          &times;
        </button>
      </div>
    );
  }
);

export default function TemplatePathBuilder({
  pathSegments,
  segmentComposer,
  assetSearchQuery,
  filteredRealAssets,
  realAssetsById,
  exampleDeploymentIdsByAssetId,
  onToggleChooser,
  onOpenLiteralComposer,
  onLiteralDraftChange,
  onCommitLiteral,
  onOpenRealAssetPicker,
  onInsertOrReplaceToken,
  onCommitExistingLiteral,
  onRemoveSegment,
  onCloseComposer,
  onAssetSearchChange,
}: TemplatePathBuilderProps) {
  const builderRef = useRef<HTMLDivElement | null>(null);

  useOutsideClick(builderRef, onCloseComposer);
  useEscapeToClose(segmentComposer !== null, onCloseComposer);

  const tokenPickerTarget =
    segmentComposer?.mode === "real-asset" ? segmentComposer.target : null;
  const tokenPickerSegment =
    segmentComposer?.mode === "real-asset" &&
    segmentComposer.target.mode === "replace" &&
    segmentComposer.target.index < pathSegments.length
      ? pathSegments[segmentComposer.target.index]
      : null;
  const chooserMenuId = "folder-config-add-segment-menu";
  const realAssetMenuId = "folder-config-real-asset-menu";

  return (
    <div ref={builderRef} className="relative">
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
        <div className="flex flex-wrap items-center gap-1.5">
          {pathSegments.map((segment, index) => {
            const isActiveToken =
              segment.kind === "token" &&
              segmentComposer?.mode === "real-asset" &&
              segmentComposer.target.mode === "replace" &&
              segmentComposer.target.index === index;
            const asset =
              segment.kind === "token"
                ? realAssetsById.get(segment.value)
                : null;

            return (
              <div
                key={`${segment.kind}-${segment.value}-${index}`}
                className="flex items-center gap-1"
              >
                {segment.kind === "literal" ? (
                  <InlineLiteralPathSegmentInput
                    value={segment.value}
                    ariaLabel={`Folder segment ${segment.value}`}
                    onCommit={(nextValue) =>
                      onCommitExistingLiteral(index, nextValue)
                    }
                    onRemove={() => onRemoveSegment(index)}
                  />
                ) : (
                  <div className="group flex items-center gap-0.5">
                    <VariableSegmentBadge
                      segment={segment}
                      realAssetsById={realAssetsById}
                      isActive={isActiveToken}
                      title={
                        asset
                          ? `${asset.name} (${realAssetTypeLabel(asset.type)})`
                          : undefined
                      }
                      ariaLabel={`Edit real asset segment ${renderSegmentLabel(
                        segment,
                        realAssetsById
                      )}`}
                      onClick={() =>
                        onOpenRealAssetPicker({
                          mode: "replace",
                          index,
                        })
                      }
                    />
                    <button
                      type="button"
                      className="rounded-full px-1 py-0.5 text-xs text-grey-100 opacity-0 transition-all hover:bg-slate-200 hover:text-grey-300 group-hover:opacity-100"
                      aria-label={`Remove segment ${renderSegmentLabel(
                        segment,
                        realAssetsById
                      )}`}
                      onClick={() => onRemoveSegment(index)}
                    >
                      &times;
                    </button>
                  </div>
                )}

                {index < pathSegments.length - 1 ? (
                  <span className="text-xs text-grey-100">/</span>
                ) : null}
              </div>
            );
          })}

          {pathSegments.length > 0 ? (
            <span className="text-xs text-grey-100">/</span>
          ) : null}

          {segmentComposer?.mode === "literal" ? (
            <>
              <InlineLiteralSegmentEditor
                draft={segmentComposer.draft}
                ariaLabel="New folder segment"
                onDraftChange={onLiteralDraftChange}
                onCommit={onCommitLiteral}
                onCancel={onCloseComposer}
              />
              <span className="text-xs text-grey-100">/</span>
            </>
          ) : null}

          {segmentComposer?.mode === "real-asset" &&
          segmentComposer.target.mode === "insert" ? (
            <>
              <div className="rounded-lg border-2 border-blue-100 bg-white px-3 py-1.5 shadow-sm">
                <div className="flex items-center gap-2 text-sm italic text-grey-200">
                  <FiLink
                    aria-hidden="true"
                    className="text-blue-200"
                    size={14}
                  />
                  <span>select asset...</span>
                </div>
              </div>
              <span className="text-xs text-grey-100">/</span>
            </>
          ) : null}

          <button
            type="button"
            className={`rounded-lg border px-3 py-1.5 text-sm font-semibold transition ${
              segmentComposer?.mode === "chooser"
                ? "border-blue-100 bg-blue-100 text-white"
                : "border-slate-300 bg-white text-grey-200 hover:border-blue-100 hover:text-blue-200"
            }`}
            aria-label="Add path segment"
            aria-expanded={segmentComposer?.mode === "chooser"}
            aria-controls={chooserMenuId}
            onClick={onToggleChooser}
          >
            +
          </button>
        </div>

        {segmentComposer?.mode === "literal" ? (
          <p className="mt-2 text-[11px] text-grey-100">
            Type a folder name and press Enter
          </p>
        ) : null}
      </div>

      {segmentComposer?.mode === "chooser" ? (
        <div
          id={chooserMenuId}
          className="absolute right-0 top-[calc(100%+10px)] z-10 w-[260px] rounded-xl border border-slate-200 bg-white shadow-xl"
        >
          <div className="px-4 pb-2 pt-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-grey-100">
              Add Path Segment
            </p>
          </div>
          <div className="space-y-0.5 px-2 pb-2">
            <button
              type="button"
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition hover:bg-slate-50"
              onClick={onOpenLiteralComposer}
            >
              <FiType
                aria-hidden="true"
                className="shrink-0 text-blue-200"
                size={18}
              />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-grey-300">
                  Literal Text
                </p>
                <p className="text-[11px] text-grey-100">
                  Type a folder name directly
                </p>
              </div>
            </button>
            <button
              type="button"
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition hover:bg-slate-50"
              onClick={() =>
                onOpenRealAssetPicker({
                  mode: "insert",
                  index: pathSegments.length,
                })
              }
            >
              <FiLink
                aria-hidden="true"
                className="shrink-0 text-blue-200"
                size={18}
              />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-grey-300">
                  Real Asset
                </p>
                <p className="text-[11px] text-grey-100">
                  Link to a registered asset
                </p>
              </div>
            </button>
          </div>
        </div>
      ) : null}

      {segmentComposer?.mode === "real-asset" ? (
        <div
          id={realAssetMenuId}
          className="absolute left-0 top-[calc(100%+10px)] z-10 w-full rounded-xl border border-slate-200 bg-white shadow-xl"
        >
          <div className="px-4 pb-2 pt-3">
            <p className="text-sm font-semibold text-grey-300">
              {tokenPickerTarget?.mode === "replace" && tokenPickerSegment
                ? `Edit ${renderSegmentLabel(
                    tokenPickerSegment,
                    realAssetsById
                  )}`
                : "Insert a real asset"}
            </p>
            <p className="mt-1 text-[11px] leading-4 text-grey-100">
              The asset&apos;s ID will be used as a variable folder name.
            </p>
          </div>
          <div className="px-3 pb-2">
            <BufferedSearchInput
              value={assetSearchQuery}
              onCommit={onAssetSearchChange}
              className="w-full rounded-md border border-slate-300 bg-white py-[6px] pl-10 pr-10 text-sm text-blue-300 placeholder:text-slate-400 focus:border-blue-400 focus:outline-none"
              placeholder="Search real assets..."
              inputId="curated-directory-token-search"
              inputName="token_search"
            />
            <div className="mt-2 max-h-[220px] overflow-auto rounded-lg border border-slate-100">
              {filteredRealAssets.length === 0 ? (
                <p className="rounded-lg px-3 py-4 text-center text-xs text-grey-200">
                  No real assets match that search.
                </p>
              ) : (
                filteredRealAssets.map((asset, assetIndex) => {
                  const assetPublicId = asset.public_id as string;
                  const exampleDeploymentId =
                    exampleDeploymentIdsByAssetId.get(assetPublicId);
                  const isSelectedAsset =
                    tokenPickerSegment?.kind === "token" &&
                    tokenPickerSegment.value === assetPublicId;

                  return (
                    <div
                      key={asset.public_id}
                      className={`flex items-center justify-between gap-3 px-3 py-2 ${
                        assetIndex > 0 ? "border-t border-slate-100" : ""
                      } ${isSelectedAsset ? "bg-blue-50" : "hover:bg-blue-50"}`}
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-blue-400">
                          {asset.name}
                        </p>
                        <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-grey-100">
                          <span
                            className={`rounded px-1.5 py-0.5 font-medium ${realAssetTypeBadgeClasses(
                              asset.type
                            )}`}
                          >
                            {realAssetTypeLabel(asset.type)}
                          </span>
                          {exampleDeploymentId ? (
                            <span className="font-mono">
                              e.g. {exampleDeploymentId}
                            </span>
                          ) : null}
                        </div>
                      </div>
                      <button
                        type="button"
                        className={`shrink-0 rounded-md px-2.5 py-1 text-xs font-semibold transition ${
                          isSelectedAsset
                            ? "bg-blue-100 text-white"
                            : "text-blue-200 hover:bg-blue-50"
                        }`}
                        onClick={() => onInsertOrReplaceToken(assetPublicId)}
                      >
                        {tokenPickerTarget?.mode === "replace"
                          ? "Replace"
                          : "Insert"}
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          </div>
          <div className="flex items-center justify-between border-t border-slate-100 px-4 py-2 text-[11px] text-grey-100">
            <span>{filteredRealAssets.length} real assets</span>
            <button
              type="button"
              className="font-medium text-grey-200 transition hover:text-grey-300"
              onClick={onCloseComposer}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
